import { GetApiIntroducerPortalIntroducerApplicationOfferFundingRequirementsResponse } from '@iwoca/lapi-client/introducer_portal';
import { t } from '@lingui/macro';

type TRequiredLoanDocument =
  GetApiIntroducerPortalIntroducerApplicationOfferFundingRequirementsResponse['data']['required_loan_documents'][number];
type TFundingRequirement =
  GetApiIntroducerPortalIntroducerApplicationOfferFundingRequirementsResponse['data']['funding_requirements'][number];

export type TDeserialisedFundingRequirement = {
  label:
    | ReturnType<typeof deserialiseRequirementResolution>
    | ReturnType<typeof deserialiseRequiredLoanDocumentsLabel>;
  type:
    | TFundingRequirement['recommended_resolution_method']
    | TRequiredLoanDocument['type'];
  isCompleted: boolean;
};

export const deserialiseFundingRequirements = (
  funding_requirements: TFundingRequirement[],
) => {
  const deserialisedFundingRequirements = funding_requirements.map(
    (funding_requirement): TDeserialisedFundingRequirement => {
      const label = deserialiseRequirementResolution(funding_requirement);
      return {
        label,
        type: funding_requirement.recommended_resolution_method,
        isCompleted: funding_requirement.complete,
      };
    },
  );

  const uniqueFundingRequirements =
    showUniqueFundingRequirementsWithCorrectStatus(
      deserialisedFundingRequirements,
    );
  return uniqueFundingRequirements;
};

function sortCompletedToEndOfArray(
  a: TDeserialisedFundingRequirement,
  b: TDeserialisedFundingRequirement,
) {
  if (a.isCompleted && !b.isCompleted) {
    return 1;
  }
  if (!a.isCompleted && b.isCompleted) {
    return -1;
  }
  return 0;
}

function showUniqueFundingRequirementsWithCorrectStatus(
  fundingRequirements: TDeserialisedFundingRequirement[],
) {
  // Since multiple requirements can have the same recommended_resolution_method, sort the completed ones
  // to the back so that the findIndex will match the incomplete ones as the unique requirement
  const uniqueRequirements = fundingRequirements
    .sort(sortCompletedToEndOfArray)
    .filter(
      (requirement, index) =>
        fundingRequirements.findIndex(
          (allRequirements) => allRequirements.label === requirement.label,
        ) === index,
    );

  return uniqueRequirements.filter((requirement) => requirement.label !== null);
}

function deserialiseRequirementResolution(
  funding_requirement: TFundingRequirement,
) {
  switch (funding_requirement.recommended_resolution_method) {
    case 'add_default_payment_method':
    case 'check_linked_direct_debit':
      return t`Debit card linked`;
    case 'link_bank_account':
    case 'complete_penny_transfer':
    case 'complete_penny_transfer_with_signature':
    case 'check_IBAN_is_fundable':
    case 'experian_bank_wizard':
      return t`Business bank details shared`;
    case 'major_shareholders_check':
      return t`Directors & shareholders confirmed`;
    case 'id_now_check':
    case 'check_customer_does_not_exceed_bip_cap':
    case 'sign_documents':
    case 'request_appropriate_loan_amount':
    case 'check_mla_covers_post_funding_balance':
      return null;
    // check_commission_consent_checkbox only returned in UK
    case 'check_commission_consent_checkbox':
      return 'Commission consent given';
    default:
      return t`KYC checks complete`;
  }
}

export const deserialiseRequiredLoanDocuments = (
  requiredLoanDocuments: TRequiredLoanDocument[],
) => {
  const deserialisedRequiredLoanDocuments = requiredLoanDocuments.map(
    (loanDocument): TDeserialisedFundingRequirement => {
      const label = deserialiseRequiredLoanDocumentsLabel(loanDocument);
      return {
        type: loanDocument.type,
        label,
        isCompleted: loanDocument.complete,
      };
    },
  );
  return deserialisedRequiredLoanDocuments.filter(
    (document) => document.label !== null,
  );
};

function deserialiseRequiredLoanDocumentsLabel(
  requiredLoanDocument: TRequiredLoanDocument,
) {
  switch (requiredLoanDocument.type) {
    case 'master_loan_agreement':
      return t`Loan agreement`;
    case 'guarantee':
      return t`Personal guarantee`;
    default:
      return null;
  }
}
